import React, { useState } from 'react';
import { Send, CheckCircle2, AlertTriangle, Loader2, ChevronUp, ChevronDown } from 'lucide-react';

export default function ContactForm({ type = 'contact' }) {
  const isBusiness = type === 'business';

  // State for standard contact form
  const [contactData, setContactData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '' // Honeypot
  });

  // State for business registration form
  const [businessData, setBusinessData] = useState({
    companyName: '',
    owner: '',
    address: '',
    postcode: '9000',
    city: '',
    companyType: 'ApS',
    cvr: '',
    bank: '',
    email: '',
    billingMethod: 'E-mail',
    termsAccepted: false,
    website_url: '' // Honeypot
  });

  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBusinessChange = (e) => {
    const { name, value } = e.target;
    setBusinessData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBusinessCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setBusinessData((prev) => ({ ...prev, [name]: checked }));
  };

  const handlePostcodeChange = (e) => {
    const val = e.target.value.replace(/\D/g, ''); // Kun tal
    setBusinessData((prev) => ({ ...prev, postcode: val }));
  };

  const incrementPostcode = () => {
    setBusinessData((prev) => {
      const current = parseInt(prev.postcode, 10) || 0;
      return { ...prev, postcode: String(Math.min(9999, current + 1)) };
    });
  };

  const decrementPostcode = () => {
    setBusinessData((prev) => {
      const current = parseInt(prev.postcode, 10) || 0;
      return { ...prev, postcode: String(Math.max(0, current - 1)) };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    const activeData = isBusiness ? businessData : contactData;

    // Honeypot tjek: hvis website_url er udfyldt, er det en bot
    if (activeData.website_url) {
      console.warn('Spam detected');
      setTimeout(() => {
        setStatus('success');
      }, 1000);
      return;
    }

    if (isBusiness && !businessData.termsAccepted) {
      setStatus('error');
      setErrorMessage('Du skal acceptere betingelserne for at fortsætte.');
      return;
    }

    try {
      const payload = isBusiness
        ? {
            firmanavn: businessData.companyName,
            ansvarlig_leder: businessData.owner,
            adresse: businessData.address,
            postnr: businessData.postcode,
            by: businessData.city,
            virksomhedsform: businessData.companyType,
            cvr_nr: businessData.cvr,
            bank_afdeling: businessData.bank,
            email_adresse: businessData.email,
            faktureringsmetode: businessData.billingMethod,
            betingelser_godkendt: businessData.termsAccepted,
            website_url: businessData.website_url
          }
        : {
            navn: contactData.name,
            telefonnummer: contactData.phone,
            email: contactData.email,
            beskrivelse: contactData.message,
            website_url: contactData.website_url
          };

      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (response.ok && result.status === 'ok') {
        setStatus('success');
        if (isBusiness) {
          setBusinessData({
            companyName: '',
            owner: '',
            address: '',
            postcode: '9000',
            city: '',
            companyType: 'ApS',
            cvr: '',
            bank: '',
            email: '',
            billingMethod: 'E-mail',
            termsAccepted: false,
            website_url: ''
          });
        } else {
          setContactData({
            name: '',
            phone: '',
            email: '',
            message: '',
            website_url: ''
          });
        }
      } else {
        throw new Error(result.message || 'Der skete en fejl ved afsendelse. Prøv venligst igen.');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setStatus('error');
      setErrorMessage(err.message || 'Kunne ikke oprette forbindelse til serveren. Prøv igen senere.');
    }
  };

  return (
    <div className="rounded-lg border border-brand-grey bg-white p-6 shadow-xl md:p-8">
      <h3 className="text-xl font-bold text-gray-900 mb-2 uppercase tracking-wide">
        {isBusiness ? 'Erhvervskundeoprettelse' : 'Send os en besked'}
      </h3>
      <p className="text-sm text-gray-600 mb-6">
        {isBusiness
          ? 'Udfyld formularen nedenfor for at blive oprettet som erhvervskunde hos os.'
          : 'Udfyld formularen nedenfor, så vender vi hurtigt tilbage til dig med et uforpligtende tilbud.'}
      </p>

      {status === 'success' ? (
        <div className="flex flex-col items-center justify-center text-center py-8 px-4 border border-emerald-500/30 bg-emerald-500/5 rounded-lg">
          <CheckCircle2 className="h-16 w-16 text-emerald-500 mb-4 animate-bounce" />
          <h4 className="text-lg font-bold text-gray-900 mb-2">
            {isBusiness ? 'Tak for din ansøgning!' : 'Tak for din besked!'}
          </h4>
          <p className="text-sm text-gray-600 max-w-md">
            {isBusiness
              ? 'Vi har modtaget dine oplysninger og vil behandle din erhvervskundeoprettelse hurtigst muligt.'
              : 'Vi har modtaget din henvendelse og vender tilbage til dig så hurtigt som muligt.'}
          </p>
          <button
            onClick={() => setStatus('idle')}
            className="mt-6 rounded-md bg-brand-light px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-brand-yellow hover:text-white transition-all duration-200 cursor-pointer"
          >
            {isBusiness ? 'Opret en ny konto' : 'Send en ny besked'}
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Honeypot felt - ABSOLUT SKJULT for mennesker */}
          <div className="sr-only absolute" aria-hidden="true">
            <label htmlFor="website_url">Do not fill this field if you are human</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={isBusiness ? businessData.website_url : contactData.website_url}
              onChange={isBusiness ? handleBusinessChange : handleContactChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          {isBusiness ? (
            /* Erhvervskundeoprettelse Formular */
            <>
              {/* Firmanavn & CVR-nr */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="companyName" className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                    Firmanavn <span className="text-brand-yellow">*</span>
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    required
                    value={businessData.companyName}
                    onChange={handleBusinessChange}
                    placeholder="Virksomhedens navn"
                    className="w-full rounded-md border border-brand-grey bg-brand-light px-4 py-3 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-brand-yellow focus:ring-1 focus:ring-brand-yellow transition-all duration-200"
                  />
                </div>

                <div>
                  <label htmlFor="cvr" className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                    CVR-nr. <span className="text-brand-yellow">*</span>
                  </label>
                  <input
                    type="text"
                    id="cvr"
                    name="cvr"
                    required
                    value={businessData.cvr}
                    onChange={handleBusinessChange}
                    placeholder="8-cifret CVR-nummer"
                    className="w-full rounded-md border border-brand-grey bg-brand-light px-4 py-3 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-brand-yellow focus:ring-1 focus:ring-brand-yellow transition-all duration-200"
                  />
                </div>
              </div>

              {/* Adresse */}
              <div>
                <label htmlFor="address" className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                  Adresse <span className="text-brand-yellow">*</span>
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  required
                  value={businessData.address}
                  onChange={handleBusinessChange}
                  placeholder="Gade og husnummer"
                  className="w-full rounded-md border border-brand-grey bg-brand-light px-4 py-3 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-brand-yellow focus:ring-1 focus:ring-brand-yellow transition-all duration-200"
                />
              </div>

              {/* Postnr. & By */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="postcode" className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                    Postnr. <span className="text-brand-yellow">*</span>
                  </label>
                  <div className="relative flex items-stretch">
                    <input
                      type="text"
                      id="postcode"
                      name="postcode"
                      required
                      value={businessData.postcode}
                      onChange={handlePostcodeChange}
                      placeholder="f.eks. 9000"
                      className="w-full rounded-l-md border border-r-0 border-brand-grey bg-brand-light px-4 py-3 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-brand-yellow focus:ring-1 focus:ring-brand-yellow transition-all duration-200"
                    />
                    <div className="flex flex-col border-y border-r border-brand-grey rounded-r-md bg-brand-light overflow-hidden">
                      <button
                        type="button"
                        onClick={incrementPostcode}
                        className="flex-1 px-2.5 hover:bg-brand-yellow hover:text-white border-b border-brand-grey text-gray-500 transition-colors duration-150 flex items-center justify-center cursor-pointer"
                        aria-label="Øg postnummer"
                      >
                        <ChevronUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={decrementPostcode}
                        className="flex-1 px-2.5 hover:bg-brand-yellow hover:text-white text-gray-500 transition-colors duration-150 flex items-center justify-center cursor-pointer"
                        aria-label="Sænk postnummer"
                      >
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="city" className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                    By <span className="text-brand-yellow">*</span>
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    required
                    value={businessData.city}
                    onChange={handleBusinessChange}
                    placeholder="By"
                    className="w-full rounded-md border border-brand-grey bg-brand-light px-4 py-3 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-brand-yellow focus:ring-1 focus:ring-brand-yellow transition-all duration-200"
                  />
                </div>
              </div>



              {/* E-mail adresse */}
              <div>
                <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                  Faktura / E-mail adresse <span className="text-brand-yellow">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={businessData.email}
                  onChange={handleBusinessChange}
                  placeholder="kontakt@virksomhed.dk"
                  className="w-full rounded-md border border-brand-grey bg-brand-light px-4 py-3 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-brand-yellow focus:ring-1 focus:ring-brand-yellow transition-all duration-200"
                />
              </div>

              {/* Faktureringsmetode */}
              <div className="pt-2 border-t border-gray-100">
                <span className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">
                  Vælg faktureringsmetode <span className="text-brand-yellow">*</span>
                </span>
                <div className="relative">
                  <select
                    id="billingMethod"
                    name="billingMethod"
                    required
                    value={businessData.billingMethod}
                    onChange={handleBusinessChange}
                    className="w-full rounded-md border border-brand-grey bg-brand-light pl-4 pr-10 py-3 text-sm text-gray-900 outline-none focus:border-brand-yellow focus:ring-1 focus:ring-brand-yellow transition-all duration-200 appearance-none cursor-pointer"
                  >
                    <option value="E-mail">E-mail</option>
                    <option value="EAN">EAN</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
                    <ChevronDown className="h-4 w-4" />
                  </div>
                </div>
              </div>

              {/* Betingelser Checkbox */}
              <div className="flex items-start pt-2">
                <div className="flex h-5 items-center">
                  <input
                    id="termsAccepted"
                    name="termsAccepted"
                    type="checkbox"
                    required
                    checked={businessData.termsAccepted}
                    onChange={handleBusinessCheckboxChange}
                    className="h-4 w-4 rounded border-brand-grey text-brand-yellow focus:ring-brand-yellow cursor-pointer"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="termsAccepted" className="font-medium text-gray-700 select-none cursor-pointer">
                    Vi har læst, og godkender betingelserne <span className="text-brand-yellow">*</span>
                  </label>
                </div>
              </div>
            </>
          ) : (
            /* Standard Kontaktformular */
            <>
              {/* Navn & Telefonnummer */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                    Navn <span className="text-brand-yellow">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={contactData.name}
                    onChange={handleContactChange}
                    placeholder="Dit fulde navn"
                    className="w-full rounded-md border border-brand-grey bg-brand-light px-4 py-3 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-brand-yellow focus:ring-1 focus:ring-brand-yellow transition-all duration-200"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                    Telefonnummer <span className="text-brand-yellow">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={contactData.phone}
                    onChange={handleContactChange}
                    placeholder="Dit telefonnummer"
                    className="w-full rounded-md border border-brand-grey bg-brand-light px-4 py-3 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-brand-yellow focus:ring-1 focus:ring-brand-yellow transition-all duration-200"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                  Email <span className="text-brand-yellow">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={contactData.email}
                  onChange={handleContactChange}
                  placeholder="din@email.dk"
                  className="w-full rounded-md border border-brand-grey bg-brand-light px-4 py-3 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-brand-yellow focus:ring-1 focus:ring-brand-yellow transition-all duration-200"
                />
              </div>

              {/* Beskrivelse */}
              <div>
                <label htmlFor="message" className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                  Beskrivelse <span className="text-brand-yellow">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows="4"
                  value={contactData.message}
                  onChange={handleContactChange}
                  placeholder="Skriv din besked her..."
                  className="w-full rounded-md border border-brand-grey bg-brand-light px-4 py-3 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-brand-yellow focus:ring-1 focus:ring-brand-yellow transition-all duration-200 resize-none"
                />
              </div>
            </>
          )}

          {/* Fejlbesked */}
          {status === 'error' && (
            <div className="flex items-start gap-2.5 rounded-md border border-red-500/30 bg-red-500/5 p-3 text-sm text-red-500">
              <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Send knap */}
          <button
            type="submit"
            disabled={status === 'submitting'}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-brand-yellow px-6 py-3.5 text-sm font-bold uppercase tracking-wider text-white hover:bg-brand-yellow-hover disabled:bg-gray-200 disabled:text-gray-400 transition-all duration-200 cursor-pointer"
          >
            {status === 'submitting' ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Sender...</span>
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                <span>{isBusiness ? 'Send ansøgning' : 'Send besked'}</span>
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
